/**
 * TermsPage - Full Terms & Conditions and Privacy Policy
 * Accessible via footer link
 */

import React from 'react';
import { Box, Typography, Sheet, Divider, IconButton } from '@mui/joy';
import { ArrowBack, Gavel, Shield, Copyright, Security, Mail } from '@mui/icons-material';

interface TermsPageProps {
  onClose?: () => void;
}

export default function TermsPage({ onClose }: TermsPageProps) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.body', pb: 6 }}>
      {/* Header */}
      <Sheet
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
          px: { xs: 2, md: 4 },
          py: 2
        }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          {onClose && (
            <IconButton size="sm" variant="plain" onClick={onClose}>
              <ArrowBack />
            </IconButton>
          )}
          <Box>
            <Typography level="h4" sx={{ fontWeight: 700 }}>
              Terms & Conditions
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Last Updated: January 1, 2026
            </Typography>
          </Box>
        </Box>
      </Sheet>

      {/* Content */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2, md: 4 },
          py: 4,
          '& h2': { fontSize: '1.5rem', fontWeight: 700, mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 },
          '& h3': { fontSize: '1.15rem', fontWeight: 700, mt: 3, mb: 1.5 },
          '& p': { mb: 1.5, lineHeight: 1.7 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 0.75 }
        }}
      >
        {/* Introduction */}
        <Typography level="body-md" sx={{ fontSize: '1.1rem', color: 'text.secondary', mb: 3 }}>
          Welcome to ShreemAstro. By using our services, you agree to comply with and be bound by the following 
          terms and conditions. Please review them carefully.
        </Typography>

        <Sheet variant="soft" color="warning" sx={{ p: 2.5, borderRadius: 'md', mb: 4 }}>
          <Typography level="title-md" sx={{ fontWeight: 700, mb: 1 }}>
            ⚠️ IMPORTANT DISCLAIMER
          </Typography>
          <Typography level="body-sm">
            ShreemAstro provides astrological guidance for entertainment and educational purposes only. 
            Our predictions and analysis are not a substitute for professional medical, legal, financial, or psychological advice. 
            We make no guarantees about the accuracy of predictions or outcomes.
          </Typography>
        </Sheet>

        <Divider sx={{ my: 3 }} />

        {/* Full Terms Content */}
        <Typography level="h2">
          <Gavel />
          1. Acceptance of Terms
        </Typography>
        <p>
          These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User", "you") 
          and ShreemAstro ("we", "us", "our") governing your access to and use of the ShreemAstro platform 
          (website, mobile applications, and related services).
        </p>
        <p>
          By creating an account, accessing the platform, or using any of our services, you acknowledge that you have 
          read, understood, and agree to be bound by these Terms, along with our Privacy Policy. If you do not agree 
          to these Terms, you must immediately cease using the Service.
        </p>

        <Typography level="h2">2. Definitions</Typography>
        <ul>
          <li><strong>Service:</strong> The ShreemAstro platform including website, applications, and all features</li>
          <li><strong>User:</strong> Any person who creates an account or uses the Service</li>
          <li><strong>Birth Chart:</strong> Astrological chart calculated based on birth date, time, and location</li>
          <li><strong>Prediction:</strong> Astrological analysis, dasha timeline, transit effects, or life guidance</li>
          <li><strong>Content:</strong> All text, charts, graphics, analysis generated by the Service</li>
        </ul>

        <Typography level="h2">
          <Shield />
          3. Nature of Astrological Services
        </Typography>
        
        <Typography level="h3">3.1 Vedic Astrology Basis</Typography>
        <p>
          ShreemAstro provides Vedic (Indian) astrology services based on ancient Jyotish principles, including:
        </p>
        <ul>
          <li>Birth chart (Rashi) and divisional chart (Varga) calculations</li>
          <li>Planetary periods (Vimshottari Dasha) and transits (Gochara)</li>
          <li>Yoga analysis (planetary combinations)</li>
          <li>Life area predictions (career, health, relationships, wealth, etc.)</li>
          <li>Remedies based on classical texts</li>
        </ul>

        <Typography level="h3">3.2 No Guarantees</Typography>
        <p>You acknowledge and agree that:</p>
        <ul>
          <li>Astrology is an interpretive art, not an exact or proven science</li>
          <li>Predictions are probabilistic insights based on planetary positions, not certainties</li>
          <li>Accuracy depends on the precision of birth details (date, time, location) you provide</li>
          <li>Free will, personal choices, and external factors influence life events</li>
          <li>No astrological reading can predict specific events with 100% accuracy</li>
          <li>Results may vary from person to person</li>
        </ul>

        <Typography level="h3">3.3 Not Professional Advice</Typography>
        <p><strong>ShreemAstro explicitly disclaims providing:</strong></p>
        <ul>
          <li><strong>Medical Advice:</strong> Our health predictions are not medical diagnoses. Consult a licensed physician for health concerns.</li>
          <li><strong>Legal Advice:</strong> We do not provide legal counsel. Consult an attorney for legal matters.</li>
          <li><strong>Financial Advice:</strong> Our wealth predictions are not investment recommendations. Consult a certified financial advisor.</li>
          <li><strong>Mental Health Treatment:</strong> We do not provide therapy or counseling. Consult a licensed mental health professional.</li>
          <li><strong>Relationship Counseling:</strong> Marriage/compatibility analysis is for guidance only, not professional counseling.</li>
        </ul>

        <Typography level="h2">4. Limitation of Liability</Typography>
        
        <Typography level="h3">4.1 Disclaimer of Warranties</Typography>
        <p>
          <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" 
          WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</strong>
        </p>
        <p>We disclaim all warranties including but not limited to:</p>
        <ul>
          <li>Warranties of merchantability, fitness for a particular purpose, non-infringement</li>
          <li>Accuracy, reliability, or completeness of astrological predictions</li>
          <li>Uninterrupted or error-free operation of the Service</li>
          <li>Security of data transmission (though we implement industry-standard measures)</li>
        </ul>

        <Typography level="h3">4.2 Limitation of Damages</Typography>
        <p>
          <strong>IN NO EVENT SHALL SHREEMASTRO BE LIABLE FOR ANY:</strong>
        </p>
        <ul>
          <li>Indirect, incidental, special, consequential, or punitive damages</li>
          <li>Loss of profits, revenue, data, or business opportunities</li>
          <li>Personal injury, emotional distress, or psychological harm</li>
          <li>Damages arising from reliance on astrological predictions</li>
          <li>Decisions made based on readings (business, marriage, medical, etc.)</li>
        </ul>
        <p>
          <strong>Our maximum liability is limited to the amount you paid for your subscription in the preceding 12 months, 
          or $100 USD, whichever is less.</strong>
        </p>

        <Typography level="h3">4.3 User Responsibility</Typography>
        <p>
          You assume full responsibility for all risks associated with using astrological guidance. You agree to:
        </p>
        <ul>
          <li>Use your own judgment when making important life decisions</li>
          <li>Verify information with qualified professionals before taking action</li>
          <li>Not hold ShreemAstro liable for any outcomes resulting from your choices</li>
        </ul>

        <Typography level="h2">
          <Security />
          5. Data Collection, Usage & Privacy
        </Typography>

        <Typography level="h3">5.1 Data We Collect</Typography>
        <p>By using ShreemAstro, you consent to our collection of:</p>
        <ul>
          <li><strong>Account Information:</strong> Email address, name, phone number (optional), password (encrypted)</li>
          <li><strong>Birth Details:</strong> Date of birth, time of birth, place of birth (for chart calculations)</li>
          <li><strong>Family Profiles:</strong> Birth details of family members you add</li>
          <li><strong>Usage Data:</strong> IP address, device type, browser, operating system, pages visited, time spent</li>
          <li><strong>Communication Data:</strong> Chat messages, AI prompts, support tickets</li>
          <li><strong>Payment Information:</strong> Transaction history (payment details handled by Razorpay, not stored by us)</li>
          <li><strong>Analytics:</strong> Tab activity, feature usage, click patterns</li>
        </ul>

        <Typography level="h3">5.2 How We Use Your Data</Typography>
        <p>Your data is used exclusively for:</p>
        <ul>
          <li><strong>Service Delivery:</strong> Calculating birth charts, generating predictions, personalized analysis</li>
          <li><strong>Account Management:</strong> Authentication, subscription management, trial tracking</li>
          <li><strong>Communication:</strong> Sending account notifications, security alerts, astrological updates</li>
          <li><strong>Security:</strong> Fraud detection, rate limiting, IP tracking for abuse prevention</li>
          <li><strong>Service Improvement:</strong> Analytics to enhance features, fix bugs, optimize performance</li>
          <li><strong>Legal Compliance:</strong> Compliance with IT Act 2000, GDPR, CCPA, and applicable laws</li>
        </ul>

        <Typography level="h3">5.3 Data Sharing & Third Parties</Typography>
        <p>
          <strong>We DO NOT sell your personal data to third parties.</strong> Data may be shared only with:
        </p>
        <ul>
          <li><strong>Service Providers:</strong> Supabase (database), Vercel (hosting), Razorpay (payments), OpenAI (AI chat) under strict confidentiality agreements</li>
          <li><strong>Law Enforcement:</strong> If legally required by court order or government authorities</li>
          <li><strong>Business Transfers:</strong> In case of merger, acquisition, or sale of assets (users will be notified)</li>
        </ul>
        <p>
          We do NOT share your birth details, astrological data, or personal information with astrologers, 
          data brokers, or marketing companies.
        </p>

        <Typography level="h3">5.4 Data Retention</Typography>
        <ul>
          <li><strong>Active Accounts:</strong> Data retained as long as your account is active</li>
          <li><strong>Deleted Accounts:</strong> Data deleted within 90 days of account deletion (except legally required records)</li>
          <li><strong>Backups:</strong> Data may persist in encrypted backups for up to 30 days after deletion</li>
        </ul>

        <Typography level="h3">5.5 Your Data Rights (GDPR/CCPA Compliance)</Typography>
        <p>You have the right to:</p>
        <ul>
          <li><strong>Access:</strong> Request a copy of your personal data</li>
          <li><strong>Correction:</strong> Update inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your account and data (right to be forgotten)</li>
          <li><strong>Export:</strong> Download your birth profiles, chat history in JSON format</li>
          <li><strong>Opt-Out:</strong> Unsubscribe from marketing emails (required notifications cannot be disabled)</li>
          <li><strong>Objection:</strong> Object to processing of your data for specific purposes</li>
        </ul>
        <p>
          To exercise these rights, contact us at <strong>support@shreemastro.com</strong>. We will respond within 30 days.
        </p>

        <Typography level="h2">
          <Mail />
          6. Communication Consent
        </Typography>

        <Typography level="h3">6.1 Email Communication</Typography>
        <p>By creating an account, you consent to receive emails for:</p>
        <ul>
          <li><strong>Transactional:</strong> Account verification, password resets, security alerts (cannot opt-out)</li>
          <li><strong>Billing:</strong> Subscription confirmations, payment receipts, renewal reminders (cannot opt-out)</li>
          <li><strong>Service Updates:</strong> Changes to Terms, new features, scheduled maintenance</li>
          <li><strong>Astrological Alerts:</strong> Important transit notifications, dasha period changes</li>
        </ul>

        <Typography level="h3">6.2 SMS Communication</Typography>
        <p>
          If you provide a phone number, you consent to receiving SMS for:
        </p>
        <ul>
          <li>Critical account security alerts (suspicious login, password change)</li>
          <li>Payment confirmations and subscription expiry warnings</li>
        </ul>
        <p>Standard message and data rates may apply. SMS frequency: max 2-3 per month.</p>

        <Typography level="h3">6.3 Marketing Communication</Typography>
        <p>
          We will send promotional emails (new features, special offers, blog posts) ONLY if you opt-in via checkbox. 
          You can unsubscribe anytime using the link in any marketing email.
        </p>

        <Typography level="h2">
          <Copyright />
          7. Intellectual Property & Copyright
        </Typography>

        <Typography level="h3">7.1 Ownership</Typography>
        <p>
          <strong>© ShreemAstro 2026. All Rights Reserved.</strong>
        </p>
        <p>
          All intellectual property rights in the Service and Content belong exclusively to ShreemAstro, including:
        </p>
        <ul>
          <li>Birth charts (North Indian, South Indian, D-charts, vargas)</li>
          <li>Astrological analysis, predictions, AI-generated insights</li>
          <li>Dasha timelines, transit reports, yoga analysis</li>
          <li>Remedy recommendations, life area overviews</li>
          <li>Text, graphics, logos, icons, user interface design</li>
          <li>Software, algorithms, source code, databases</li>
          <li>Trademark "ShreemAstro" and associated branding</li>
        </ul>
        <p>
          Content is protected under:
        </p>
        <ul>
          <li><strong>India:</strong> Copyright Act 1957, Information Technology Act 2000, Trademark Act 1999</li>
          <li><strong>International:</strong> DMCA (USA), WIPO Copyright Treaty, Berne Convention</li>
        </ul>

        <Typography level="h3">7.2 Permitted Use</Typography>
        <p>You may:</p>
        <ul>
          <li>View and use Content solely for your personal, non-commercial purposes</li>
          <li>Print one copy of your birth chart for personal records</li>
        </ul>

        <Typography level="h3">7.3 Prohibited Actions</Typography>
        <p><strong>The following are strictly prohibited and may result in account termination and legal action:</strong></p>
        <ul>
          <li>Taking screenshots or screen recordings of charts, analysis, or predictions</li>
          <li>Copying, reproducing, distributing, or republishing any Content</li>
          <li>Sharing your login credentials with others</li>
          <li>Using Content for commercial purposes (selling readings, consulting services)</li>
          <li>Creating derivative works based on our Content</li>
          <li>Reverse engineering, decompiling, or extracting algorithms</li>
          <li>Web scraping, data mining, or automated access (bots)</li>
          <li>Removing copyright notices or watermarks</li>
        </ul>

        <Typography level="h3">7.4 DMCA Takedown</Typography>
        <p>
          If you believe your copyrighted work has been infringed, contact our DMCA Agent at 
          <strong> legal@shreemastro.com</strong> with:
        </p>
        <ul>
          <li>Description of the copyrighted work</li>
          <li>Location of infringing material</li>
          <li>Your contact information and signature</li>
        </ul>

        <Typography level="h2">8. User Responsibilities</Typography>
        <ul>
          <li><strong>Accurate Information:</strong> Provide correct birth details for reliable readings</li>
          <li><strong>Account Security:</strong> Keep your password confidential, enable 2FA if available</li>
          <li><strong>One Account Per Person:</strong> Do not create multiple accounts to abuse trials/free features</li>
          <li><strong>Lawful Use:</strong> Do not use the Service for illegal activities or to harm others</li>
          <li><strong>Respect:</strong> Be respectful in chat/support interactions</li>
          <li><strong>Reporting:</strong> Report bugs, security vulnerabilities to support@shreemastro.com</li>
        </ul>

        <Typography level="h2">9. Account Termination</Typography>

        <Typography level="h3">9.1 Termination by You</Typography>
        <p>
          You may delete your account anytime via Settings. Upon deletion:
        </p>
        <ul>
          <li>Your access to the Service will cease immediately</li>
          <li>Your data will be deleted within 90 days (except legally required records)</li>
          <li>Active subscriptions will be cancelled (no pro-rated refunds)</li>
        </ul>

        <Typography level="h3">9.2 Termination by Us</Typography>
        <p>
          We reserve the right to suspend or terminate your account immediately, without notice, if you:
        </p>
        <ul>
          <li>Violate these Terms & Conditions</li>
          <li>Engage in fraudulent activity (fake payment methods, chargebacks)</li>
          <li>Abuse trial/free features (multiple accounts, bot usage)</li>
          <li>Share copyrighted Content publicly or commercially</li>
          <li>Attempt to hack, disrupt, or compromise the Service</li>
          <li>Engage in abusive behavior towards staff or users</li>
        </ul>
        <p>
          Terminated accounts forfeit all subscription benefits without refund.
        </p>

        <Typography level="h2">10. Subscription & Payment Terms</Typography>

        <Typography level="h3">10.1 Plans & Pricing</Typography>
        <ul>
          <li><strong>Free Trial:</strong> One-time per user (tracked by email, IP, phone)</li>
          <li><strong>Paid Plans:</strong> Silver, Gold (Monthly/Yearly) with varying features and limits</li>
          <li>Prices listed on website, subject to change with 30 days notice</li>
        </ul>

        <Typography level="h3">10.2 Auto-Renewal</Typography>
        <ul>
          <li>Subscriptions auto-renew unless cancelled before renewal date</li>
          <li>Payment processed via Razorpay (credit/debit card, UPI, net banking)</li>
          <li>Email reminders sent 7 days before renewal</li>
        </ul>

        <Typography level="h3">10.3 Refund Policy</Typography>
        <ul>
          <li><strong>Trial Period:</strong> Full refund if cancelled within 7-14 days (plan-specific)</li>
          <li><strong>After Trial:</strong> No refunds for partially used subscription periods</li>
          <li><strong>Technical Issues:</strong> Refunds considered on case-by-case basis</li>
          <li><strong>Chargebacks:</strong> Disputing valid charges may result in account termination</li>
        </ul>

        <Typography level="h3">10.4 Cancellation</Typography>
        <p>
          Cancel anytime via Settings → Subscription. Cancellation is effective at the end of the current billing period. 
          You retain access until expiry.
        </p>

        <Typography level="h2">11. Service Availability & Modifications</Typography>
        <ul>
          <li>We strive for 99.9% uptime but do not guarantee uninterrupted availability</li>
          <li>Scheduled maintenance will be announced in advance when possible</li>
          <li>We reserve the right to modify, suspend, or discontinue features without liability</li>
          <li>New features may be added, existing features may change</li>
        </ul>

        <Typography level="h2">12. Changes to Terms</Typography>
        <p>
          ShreemAstro may update these Terms at any time. Changes will be effective upon posting to the website. 
          If changes are material (affecting user rights or obligations), we will:
        </p>
        <ul>
          <li>Notify you via email at least 30 days in advance</li>
          <li>Require re-acceptance via popup overlay</li>
          <li>Update the "Last Updated" date at the top</li>
        </ul>
        <p>
          Continued use of the Service after notification constitutes acceptance of revised Terms. If you do not agree, 
          you must cease using the Service and may delete your account for a pro-rated refund (if within trial period).
        </p>

        <Typography level="h2">13. Governing Law & Dispute Resolution</Typography>

        <Typography level="h3">13.1 Governing Law</Typography>
        <p>
          These Terms are governed by and construed in accordance with the laws of India, without regard to conflict 
          of law principles. Specifically:
        </p>
        <ul>
          <li>Information Technology Act, 2000</li>
          <li>Indian Contract Act, 1872</li>
          <li>Copyright Act, 1957</li>
          <li>Consumer Protection Act, 2019</li>
        </ul>

        <Typography level="h3">13.2 Jurisdiction</Typography>
        <p>
          Any disputes arising out of these Terms shall be subject to the exclusive jurisdiction of the courts in 
          [Your City, India]. For international users, disputes may be resolved under the jurisdiction of their local courts.
        </p>

        <Typography level="h3">13.3 Dispute Resolution</Typography>
        <p>Before initiating legal action, you agree to:</p>
        <ol>
          <li><strong>Contact Support:</strong> Email support@shreemastro.com describing the issue</li>
          <li><strong>Good Faith Negotiation:</strong> Attempt to resolve amicably within 30 days</li>
          <li><strong>Mediation:</strong> If unresolved, seek mediation before litigation</li>
        </ol>

        <Typography level="h2">14. Severability</Typography>
        <p>
          If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall 
          remain in full force and effect.
        </p>

        <Typography level="h2">15. Entire Agreement</Typography>
        <p>
          These Terms, together with our Privacy Policy, constitute the entire agreement between you and ShreemAstro 
          regarding use of the Service, superseding any prior agreements.
        </p>

        <Typography level="h2">16. Contact Information</Typography>
        <p>For questions about these Terms, contact us at:</p>
        <ul>
          <li><strong>Email:</strong> support@shreemastro.com</li>
          <li><strong>Legal:</strong> legal@shreemastro.com</li>
          <li><strong>DMCA Agent:</strong> legal@shreemastro.com</li>
          <li><strong>Website:</strong> www.shreemastro.com</li>
        </ul>

        <Divider sx={{ my: 4 }} />

        <Sheet variant="soft" sx={{ p: 3, borderRadius: 'md', textAlign: 'center' }}>
          <Typography level="body-sm" sx={{ fontWeight: 600, mb: 1 }}>
            Terms & Conditions Version 1.0
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            Effective Date: January 1, 2026 | Last Updated: January 1, 2026
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
            © ShreemAstro 2026. All Rights Reserved.
          </Typography>
        </Sheet>
      </Box>
    </Box>
  );
}
